import type { PlatformCapabilities, PlatformProfile } from './types';

/**
 * Module 42 (WP-42.4) — the runtime capability seam.
 *
 * ## Why this exists
 *
 * WP-42.2 gave the build tooling one source of truth. This gives the *running
 * application* the same thing. Without it, every platform difference that shows
 * up in shared code has to be invented locally — an `import.meta.env` check
 * here, a `platform === 'crazygames'` there — and each one has to be found and
 * edited again when a third platform arrives. The ones nobody finds are the
 * bugs; that is finding #7 of the module audit, stated as code.
 *
 * ## The shape
 *
 * The factory bakes this profile's capability record into the bundle as a
 * `define`. Shared code reads it through {@link getPlatformCapabilities} and
 * nothing else. There is no setup step a composition root can forget, because
 * the value is already in the bundle by the time any module runs.
 *
 * ## What is deliberately NOT here: the platform's identity
 *
 * `id` is not part of the runtime record, and that omission is the whole point
 * of rule 3 of `Platform_Export_Rules.md`. Shared code cannot compare against a
 * platform it is never told about. If a component needs to behave differently
 * somewhere, the answer is a new capability the profiles declare — not a new
 * branch on a name. Only the registry and the composition roots know which
 * platform they are, and an ESLint rule keeps it that way.
 *
 * `displayName` *is* here: branding is data (rule 10), which is how the same
 * component renders "PuzzleMania" and "Blockway" with no conditional in it.
 */

/**
 * Everything a running build knows about its own platform.
 *
 * A superset of the profile's `capabilities` plus its display name — and
 * pointedly not its `id`.
 */
export interface RuntimePlatformCapabilities extends PlatformCapabilities {
  /** What a player of this platform calls the product. */
  displayName: string;
}

/**
 * Name of the global the build injects the record into.
 *
 * Exported so the Vite factory and this module cannot drift apart on the
 * spelling — the failure mode would be an accessor that always throws, in a
 * real build, at boot.
 */
export const PLATFORM_CAPABILITIES_GLOBAL = '__PLATFORM_CAPABILITIES__';

/**
 * The runtime record for a profile. Pure — the factory calls it to build the
 * `define`, tests call it to initialise without a build.
 */
export function runtimeCapabilitiesFor(profile: PlatformProfile): RuntimePlatformCapabilities {
  return freeze({
    ...profile.capabilities,
    displayName: profile.displayName,
  });
}

/**
 * Injected by `createPlatformViteConfig`'s `define`. Declared module-locally
 * rather than as a global `.d.ts` entry so that no file outside this one can
 * reference it even by accident — the seam is the accessor, not the global.
 */
declare const __PLATFORM_CAPABILITIES__: RuntimePlatformCapabilities | undefined;

let current: RuntimePlatformCapabilities | null = null;

function freeze(caps: RuntimePlatformCapabilities): RuntimePlatformCapabilities {
  return Object.freeze({ ...caps, locales: Object.freeze([...caps.locales]) });
}

function readInjected(): RuntimePlatformCapabilities | null {
  // `define` is a *textual* substitution, so in a real build this compiles to
  // `typeof {…} === 'undefined'` and folds to false. Outside a build — vitest,
  // a node script — the identifier does not exist at all, and only `typeof`
  // survives that; a bare reference would throw a ReferenceError instead of
  // reaching the explanatory error below.
  if (typeof __PLATFORM_CAPABILITIES__ === 'undefined') return null;
  return __PLATFORM_CAPABILITIES__;
}

/**
 * Set the capability record explicitly.
 *
 * A build never needs this. It exists for the two places that have no `define`:
 * tests, and any node-side tool that wants to reason about a profile the way
 * the app would.
 */
export function initPlatformCapabilities(caps: RuntimePlatformCapabilities): void {
  current = freeze(caps);
}

/**
 * The capabilities of the platform this bundle was built for.
 *
 * Throws rather than defaulting when it has no record. A default here would be
 * a lie with the shape of a success: the app would silently run the *other*
 * platform's behaviour — a Shop button on CrazyGames, or ads on the main site —
 * and nothing would go red. Rule 5 of the frozen doc applies to runtime
 * fallbacks exactly as it does to skipped checks.
 */
export function getPlatformCapabilities(): RuntimePlatformCapabilities {
  if (current) return current;

  const injected = readInjected();
  if (injected) {
    const frozen = freeze(injected);
    current = frozen;
    return frozen;
  }

  throw new Error(
    `Platform capabilities are not available. In a build they arrive through the ` +
      `\`${PLATFORM_CAPABILITIES_GLOBAL}\` define set by createPlatformViteConfig(). ` +
      `Outside a build (a test, a node script) call ` +
      `initPlatformCapabilities(runtimeCapabilitiesFor(profile)) first.`,
  );
}

/** Drop any initialised record. Tests only — a build has nothing to reset. */
export function resetPlatformCapabilities(): void {
  current = null;
}
